import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { useActiveHostEvent } from '@/hooks/use-active-host-event';
import BellIcon from '@/components/BellIcon';
import {
  LayoutDashboard, Image, PlusCircle, Users, Settings,
  Calendar, Upload, Grid3X3, User, LogOut
} from 'lucide-react';

export default function Navbar() {
  const { user, signOut, status } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { getActiveEventId } = useActiveHostEvent();

  if (status !== 'signed-in' || !user) return null;

  const isHost = user.role === 'HOST' || user.role === 'SUPER_ADMIN';
  const eid = isHost ? getActiveEventId() : '';

  const HOST_TABS = [
    { to: '/host-dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: eid ? `/host-vault?event_id=${eid}` : '/host-vault', icon: Image, label: 'Vault' },
    { to: '/create-event', icon: PlusCircle, label: 'Create' },
    { to: eid ? `/host-guests?event_id=${eid}` : '/host-guests', icon: Users, label: 'Guests' },
    { to: '/settings', icon: Settings, label: 'Settings' },
  ];

  const USER_TABS = [
    { to: '/contrib-event', icon: Calendar, label: 'Event' },
    { to: '/contrib-upload', icon: Upload, label: 'Upload' },
    { to: '/my-views', icon: Grid3X3, label: 'My Views' },
    { to: '/settings', icon: User, label: 'Profile' },
  ];

  const tabs = isHost ? HOST_TABS : USER_TABS;

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-[#E3E8EF] h-14"
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto h-full px-4 flex items-center justify-between">
        {/* Logo / Wordmark */}
        <Link to="/" className="flex items-center gap-2 no-underline" data-testid="nav-wordmark">
          <img src="/allviews-mark.svg" alt="" className="h-6 w-6" />
          <img src="/allviews-wordmark.svg" alt="ALLVIEWS" className="h-4" />
        </Link>

        {/* Center tabs — desktop */}
        <div className="hidden md:flex items-center gap-1">
          {tabs.map(({ to, icon: Icon, label }) => {
            const basePath = to.split('?')[0];
            const active = location.pathname === basePath || location.pathname.startsWith(basePath + '/');
            return (
              <Link
                key={label}
                to={to}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors no-underline ${
                  active
                    ? 'bg-[#EFF5FF] text-[#146BFF]'
                    : 'text-[#5B6472] hover:bg-[#F3F5F8] hover:text-[#0B1B46]'
                }`}
                data-testid={`nav-${label.toLowerCase()}`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </Link>
            );
          })}
        </div>

        {/* Right side */}
        <div className="flex items-center gap-2">
          <BellIcon />
          <span className="hidden sm:inline text-sm text-[#5B6472]" data-testid="nav-user-name">
            {user.first_name}
          </span>
          <button
            onClick={async () => { await signOut(); navigate('/'); }}
            className="p-2 rounded-full hover:bg-slate-100 transition-colors focus:outline-none focus:ring-2 focus:ring-[#146BFF]"
            data-testid="nav-sign-out-btn"
            aria-label="Sign out"
          >
            <LogOut className="w-4 h-4 text-[#5B6472]" />
          </button>
        </div>
      </div>

      {/* Mobile bottom tab bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-[#E3E8EF] flex justify-around py-1.5 px-2">
        {tabs.map(({ to, icon: Icon, label }) => {
          const basePath = to.split('?')[0];
          const active = location.pathname === basePath || location.pathname.startsWith(basePath + '/');
          return (
            <Link
              key={label}
              to={to}
              className={`flex flex-col items-center gap-0.5 px-2 py-1 text-[10px] font-semibold no-underline transition-colors ${
                active ? 'text-[#146BFF]' : 'text-[#5B6472]'
              }`}
              data-testid={`mobile-nav-${label.toLowerCase()}`}
            >
              <Icon className="w-5 h-5" />
              {label}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
